import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, X } from "lucide-react";
import { api } from "@/lib/api";

const dismissKey = (name) => `stjames.waNudgeDismissed.${name}`;
const WEEK = 7 * 24 * 3600 * 1000;

export default function WhatsAppNudge({ session, onSessionUpdate, onGoToFamily }) {
  const [setup, setSetup] = useState(null);
  const [phone, setPhone] = useState("+91 ");
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(
    () => Date.now() - Number(localStorage.getItem(dismissKey(session.name)) || 0) < WEEK
  );

  useEffect(() => {
    api.getWhatsAppSetup().then(setSetup).catch(() => setSetup({ enabled: false }));
  }, []);

  if (session.phone || hidden || !setup?.enabled) return null;

  const dismiss = () => {
    localStorage.setItem(dismissKey(session.name), String(Date.now()));
    setHidden(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const clean = phone.replace(/[^\d+]/g, "");
    if (!/^\+?\d{8,15}$/.test(clean) || clean === "+91") {
      toast.error("Enter a valid WhatsApp number with country code");
      return;
    }
    setSaving(true);
    try {
      const member = await api.login(session.name, session.email, clean);
      onSessionUpdate(member);
      toast.success("WhatsApp number saved. You'll get alerts now.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save number");
    } finally {
      setSaving(false);
    }
  };

  const sandbox = setup.sandbox_number || "";
  const waLink = setup.join_code
    ? `https://wa.me/${sandbox.replace("+", "")}?text=${encodeURIComponent(`join ${setup.join_code}`)}`
    : `https://wa.me/${sandbox.replace("+", "")}`;

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative bg-[#1A2F24] text-[#FAF9F6] p-6 sm:p-8 grain"
      data-testid="whatsapp-nudge"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Not now"
        data-testid="whatsapp-nudge-dismiss"
        className="absolute top-3 right-3 p-2 text-[#FAF9F6]/60 hover:text-[#FAF9F6] transition-colors min-h-[44px] min-w-[44px]"
      >
        <X className="h-4 w-4 mx-auto" />
      </button>

      <div className="flex items-start gap-4">
        <MessageCircle className="h-6 w-6 shrink-0 mt-1 text-[#E8F0E4]" />
        <div className="flex-1 min-w-0">
          <p className="tracker text-[10px] text-[#FAF9F6]/70">You're missing the WhatsApp alerts</p>
          <h2 className="font-display text-2xl sm:text-3xl mt-1 leading-tight">
            {session.name}, add your number and the house will text you.
          </h2>
          <p className="font-body text-sm text-[#FAF9F6]/75 mt-3 max-w-xl">
            Bookings, cancellations and the Friday digest land on WhatsApp for everyone who's joined. Two small steps:
          </p>

          <ol className="mt-5 space-y-5">
            <li className="flex gap-4">
              <span className="font-display text-2xl text-[#E8F0E4]/80 leading-none">1</span>
              <div className="flex-1">
                <p className="font-body text-sm">
                  Text <span className="font-medium">{setup.join_code ? `join ${setup.join_code}` : "the join code"}</span> to{" "}
                  <span className="font-medium whitespace-nowrap">{sandbox}</span> on WhatsApp — once.
                  {!setup.join_code && <span className="text-[#FAF9F6]/70"> Ask Aryavrat for the two-word code.</span>}
                </p>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="whatsapp-join-link"
                  className="inline-block mt-2 border border-[#FAF9F6]/40 px-4 py-2 font-body text-[11px] tracker hover:bg-[#FAF9F6] hover:text-[#1A2F24] transition-colors duration-300"
                >
                  Open WhatsApp
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="font-display text-2xl text-[#E8F0E4]/80 leading-none">2</span>
              <form onSubmit={save} className="flex-1 flex flex-col sm:flex-row gap-3">
                <input
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98xxx xxxxx"
                  data-testid="whatsapp-nudge-phone"
                  className="flex-1 bg-transparent border-b border-[#FAF9F6]/50 py-2 font-body text-base text-[#FAF9F6] placeholder:text-[#FAF9F6]/40 outline-none focus:border-[#FAF9F6]"
                />
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="whatsapp-nudge-save"
                  className="bg-[#FAF9F6] text-[#1A2F24] px-5 py-3 font-body text-[11px] tracker hover:bg-[#E8F0E4] transition-colors duration-300 disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save my number"}
                </button>
              </form>
            </li>
          </ol>
          {onGoToFamily && (
            <button
              type="button"
              onClick={onGoToFamily}
              data-testid="whatsapp-nudge-goto-family"
              className="mt-5 font-body text-[11px] tracker text-[#FAF9F6]/70 hover:text-[#FAF9F6] transition-colors underline underline-offset-4"
            >
              Adding numbers for the others? Open the Family tab →
            </button>
          )}
        </div>
      </div>
    </motion.section>
  );
}
